
import { Component, Vue, Prop } from 'vue-property-decorator';
import MarkdownIt from 'markdown-it';
import * as markdownItEmoji from 'markdown-it-emoji';
import markdownItTaskLists from 'markdown-it-task-lists';
import markdownItAnchor from 'markdown-it-anchor';
import markdownItLinkAttributes from 'markdown-it-link-attributes';
import markdownItHighlightJS from 'markdown-it-highlightjs';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';

interface ContentItem {
  type: 'markdown' | 'plaintext' | 'datatable' | 'chart' | 'code' | 'image' | 'html' | 'json' | 'suggestion';
  content: any;
  metadata?: any;
}

interface TableContent {
  headers?: string[];
  rows?: string[][];
}

@Component
export default class MessageRenderer extends Vue {
  @Prop({ required: true })
  public contentItems!: ContentItem[];

  public tableSearch: string = '';

  private markdownRenderer: MarkdownIt;

  constructor() {
    super();

    // Initialize markdown-it with plugins
    this.markdownRenderer = new MarkdownIt({
      html: true,
      linkify: true,
      typographer: true,
      breaks: true,
      highlight: (str: string, lang: string) => {
        if (lang && hljs.getLanguage(lang)) {
          try {
            return '<pre class="hljs"><code>' +
                   hljs.highlight(str, { language: lang, ignoreIllegals: true }).value +
                   '</code></pre>';
          } catch (__) {
    }
        }
        return '<pre class="hljs"><code>' + this.escapeHtml(str) + '</code></pre>';
      }
    })
    .use(markdownItEmoji.full)
    .use(markdownItTaskLists, { enabled: true, label: true, labelAfter: true })
    .use(markdownItAnchor, { permalink: false })
    .use(markdownItLinkAttributes, {
      attrs: {
        target: '_blank',
        rel: 'noopener noreferrer'
      }
    })
    .use(markdownItHighlightJS, { auto: true, code: true });
  }

  /**
   * Escape HTML helper
   */
  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Get the Shopify store name from localStorage
   */
  private getShopifyStoreName(): string | null {
    try {
      // Primary source: localStorage set by App.vue
      const storedShopName = localStorage.getItem('shopifyStoreName');
      if (storedShopName) {
        return storedShopName;
      }
      return null;
    } catch (error) {
      console.warn('Could not get Shopify store name:', error);
      return null;
    }
  }

  /**
   * Convert Assisty URL to Shopify embedded app URL
   */
  private convertToShopifyEmbeddedUrl(url: string): string {
    const shopifyStoreName = this.getShopifyStoreName();

    if (!shopifyStoreName) {
      // If we can't get the store name, return the original URL
      return url;
    }

    // Parse the URL
    try {
      const urlObj = new URL(url);

      // Check if it's an Assisty URL that should be converted
      if (urlObj.hostname === 'my.assisty.ai' || urlObj.hostname === 'demo.assisty.ai') {
        // Extract the store name without .myshopify.com
        const storeName = shopifyStoreName.replace('.myshopify.com', '');

        // Build the Shopify admin URL
        const shopifyUrl = `https://admin.shopify.com/store/${storeName}/apps/assisty${urlObj.pathname}${urlObj.search}${urlObj.hash}`;

        return shopifyUrl;
      }
    } catch (error) {
      console.warn('Error parsing URL:', error);
    }

    // Return original URL if not an Assisty URL or if parsing failed
    return url;
  }


  /**
   * Render markdown content to HTML using markdown-it
   */
  public renderMarkdown(content: string): string {
    if (!content) return '';

    try {
      // Pre-process content to convert Assisty URLs
      const processedContent = this.preprocessContent(content);

      // Render markdown with all plugins
      const rendered = this.markdownRenderer.render(processedContent);

      // Post-process to convert any remaining URLs in the HTML
      const withConvertedUrls = this.postProcessUrls(rendered);

      // Add custom styling for tables
      const withStyledTables = withConvertedUrls.replace(
        /<table>/g,
        '<table class="markdown-table">'
      );

      return withStyledTables;
    } catch (error) {
      console.error('Error rendering markdown:', error);
      // Fallback to escaping HTML
      return this.escapeHtml(content).replace(/\n/g, '<br>');
    }
  }

  /**
   * Pre-process content to convert plain Assisty URLs to markdown links
   */
  private preprocessContent(content: string): string {
    // This regex matches URLs that start with https://my.assisty.ai or https://demo.assisty.ai
    const urlRegex = /(https?:\/\/(my\.assisty\.ai|demo\.assisty\.ai)[^\s\)>\]]*)/g;

    return content.replace(urlRegex, (match, url) => {
      // Check if this URL is already part of a markdown link
      const beforeIndex = content.lastIndexOf(match);
      if (beforeIndex > 0) {
        const before = content.substring(Math.max(0, beforeIndex - 2), beforeIndex);
        if (before.endsWith('](') || before.endsWith('](')) {
          return match; // Already in a markdown link
        }
      }

      // Convert the URL for Shopify embedded app
      const convertedUrl = this.convertToShopifyEmbeddedUrl(url);

      // If URL was converted, use it; otherwise keep original
      if (convertedUrl !== url) {
        return convertedUrl;
      }

      return match;
    });
  }

  /**
   * Post-process HTML to convert any Assisty URLs in href attributes
   */
  private postProcessUrls(html: string): string {
    // Replace href attributes containing Assisty URLs
    const hrefRegex = /href="(https?:\/\/(my\.assisty\.ai|demo\.assisty\.ai)[^"]*)"/g;

    return html.replace(hrefRegex, (match, url) => {
      const convertedUrl = this.convertToShopifyEmbeddedUrl(url);
      return `href="${convertedUrl}"`;
    });
  }

  /**
   * Get table headers for v-data-table
   */
  public getTableHeaders(content: any): any[] {
    if (!content || typeof content !== 'object') return [];

    const tableData = content as TableContent;
    if (!tableData.headers) return [];

    return tableData.headers.map((header: string, index: number) => ({
      text: header,
      value: `col${index}`,
      align: 'start',
      sortable: true,
    }));
  }

  /**
   * Get table rows for v-data-table
   */
  public getTableRows(content: any): any[] {
    if (!content || typeof content !== 'object') return [];

    const tableData = content as TableContent;
    if (!tableData.rows) return [];

    return tableData.rows.map((row: string[]) => {
      const rowObj: any = {};
      row.forEach((cell: string, index: number) => {
        rowObj[`col${index}`] = cell;
      });
      return rowObj;
    });
  }

  /**
   * Format JSON content for display
   */
  public formatJson(content: any): string {
    try {
      if (typeof content === 'string') {
        return JSON.stringify(JSON.parse(content), null, 2);
      }
      return JSON.stringify(content, null, 2);
    } catch (error) {
      return String(content);
    }
  }

  /**
   * Export table data as CSV
   */
  public exportTableData(content: any): void {
    if (!content || typeof content !== 'object') return;

    const tableData = content as TableContent;
    if (!tableData.headers || !tableData.rows) return;

    // Create CSV content
    const csv: string[] = [];

    // Add headers
    csv.push(tableData.headers.map(h => `"${h}"`).join(','));

    // Add rows
    tableData.rows.forEach(row => {
      csv.push(row.map(cell => `"${cell}"`).join(','));
    });

    // Create blob and download
    const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'table-data.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Switch to orchestrator agent
   */
  public switchToOrchestrator(): void {
    // Emit event to parent component to switch agent
    this.$emit('switch-agent', 'orchestrator');
  }

  /**
   * Open agents tab
   */
  public openAgentsTab(): void {
    // Emit event to parent component to open agents tab
    this.$emit('open-agents-tab');
  }
}
